import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaSeisM(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // bg
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // layout paddings
      const padding = 24;
      let y = padding;

      // compute left/right widths early so title fits inside the left column
      const usableW = Math.max(0, width - padding * 2);
      const interGap = 8; // gap between columns
      const leftAreaW = Math.round(usableW * 0.70);
      const rightAreaW = Math.max(0, usableW - leftAreaW - interGap);
      const leftAreaX = padding;
      const rightAreaX = leftAreaX + leftAreaW + interGap;

      // draw left (gray) and right (white) column backgrounds before the title
      ctx.fillStyle = opts.leftAreaBg || '#ffffff';
      ctx.fillRect(leftAreaX, 0, leftAreaW, height);

      ctx.fillStyle = opts.rightAreaBg || '#ffffff';
      ctx.fillRect(rightAreaX, 0, rightAreaW, height);

      // Helper functions for text wrapping and justified alignment
      const wrapLines = (c, text, maxWidth) => {
        const words = String(text || '').split(' ');
        const lines = [];
        let line = '';
        for (let i = 0; i < words.length; i++) {
          const test = line ? line + ' ' + words[i] : words[i];
          if (c.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = words[i];
          } else {
            line = test;
          }
        }
        if (line) lines.push(line);
        return lines;
      };

      const drawJustifiedLine = (c, text, x, y, maxWidth) => {
        const words = String(text || '').split(' ');
        if (words.length <= 1) {
          c.fillText(text, x, y);
          return;
        }
        let wordsWidth = 0;
        words.forEach((w) => {
          wordsWidth += c.measureText(w).width;
        });
        const extraSpace = Math.max(0, maxWidth - wordsWidth);
        const gap = extraSpace / (words.length - 1);
        let xPos = x;
        words.forEach((w, idx) => {
          c.fillText(w, xPos, y);
          xPos += c.measureText(w).width + (idx < words.length - 1 ? gap : 0);
        });
      };

      const drawJustifiedLines = (c, lines, x, y0, maxWidth, lh) => {
        let yPos = y0;
        lines.forEach((line, idx) => {
          const isLast = idx === lines.length - 1;
          if (isLast) {
            c.fillText(line, x, yPos);
          } else {
            drawJustifiedLine(c, line, x, yPos, maxWidth);
          }
          yPos += lh;
        });
        return yPos;
      };

      // title square + title (placed inside the left column with a small inner inset)
      const titleFontSize = opts.titleFontSize || 45;
      const squareSize = typeof opts.titleSquareSize === 'number' ? opts.titleSquareSize : Math.round(titleFontSize * 0.9);
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 18;
      const squareText = opts.titleSquareText || '1.5';
      const innerLeftPad = 8;
      const squareX = leftAreaX + innerLeftPad;

      // square (inside left column)
      ctx.fillStyle = opts.titleSquareBg || '#0ea5e9';
      ctx.fillRect(squareX, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#ffffff';
      ctx.font = `bold ${Math.round(squareSize * 0.55)}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, squareX + squareSize / 2, y + squareSize / 2);

      // title (wrapped/justified) constrained to left column
      const title = opts.title || props.title || '';
      const titleX = squareX + squareSize + squareGap;
      const titleMaxW = Math.min(
        typeof opts.titleMaxWidth === 'number' ? opts.titleMaxWidth : width - (squareX + squareSize + squareGap) - padding,
        Math.max(0, width - (squareX + squareSize + squareGap) - padding)
      );

      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const justifyAndWrap = (c, text, x, y0, maxW, lineH) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w); curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w); curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w]; curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });

        let y = y0;
        for (let i = 0; i < lines.length; i++) {
          const ln = lines[i];
          if (i < lines.length - 1 && ln.words.length > 1) {
            // justify
            const gaps = ln.words.length - 1;
            const extra = (maxW - ln.wordsWidth) / gaps;
            let cx = x;
            for (let j = 0; j < ln.words.length; j++) {
              c.fillText(ln.words[j], cx, y);
              cx += c.measureText(ln.words[j]).width + spaceW + extra;
            }
          } else {
            c.fillText(ln.words.join(' '), x, y);
          }
          y += lineH;
        }
        return y;
      };

      const titleLineH = Math.round(titleFontSize * 1.2);
      const afterTitleY = justifyAndWrap(ctx, title, titleX, y, titleMaxW, titleLineH);

      // subtitle under title
      const subTitle = opts.subTitle || props.subTitle || '';
      if (subTitle) {
        const subFontSize = typeof opts.subTitleFontSize === 'number' ? opts.subTitleFontSize : 30;
        ctx.font = `${subFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.subTitleColor || '#475569';
        const subLineH = Math.round(subFontSize * 1.2);
        const afterSubY = justifyAndWrap(ctx, subTitle, titleX, afterTitleY + 8, titleMaxW, subLineH);
        y = Math.max(y + squareSize, afterSubY) + 24;
      } else {
        y = Math.max(y + squareSize, afterTitleY) + 24;
      }

      // draw two charts on left/right: graficaUno (pie) and graficaDos (bars)
      const graficaUnoData = opts.graficaUnoData || props.graficaUnoData || [];
      const graficaDosData = opts.graficaDosData || props.graficaDosData || [];

      // reuse the left/right layout computed earlier (leftAreaW, rightAreaW, leftAreaX, rightAreaX)
      // no need to recompute usableW or redraw the column backgrounds here

      const chartGap = 24;
      // start charts a fixed distance below the subtitle/title block
      const chartsTopGap = typeof opts.chartsTopGap === 'number' ? opts.chartsTopGap : 50;
      const chartsStartY = y + chartsTopGap;

      // --- Place both charts inside the left column ---
      // split left column horizontally into two sub-areas (pie | bars)
      const subGap = 12;
      const subUsable = Math.max(0, leftAreaW - 16); // left area inner padding
      const leftSubW = Math.floor((subUsable - subGap) / 2);
      const pieAreaX = leftAreaX + 8;
      const barsAreaX = pieAreaX + leftSubW + subGap;

      // graficaUno (pie) inside pieArea (left half of left column)
      const maxPieByWidth = Math.max(0, leftSubW - 16); // inner padding
      const maxPieByHeight = Math.max(0, height - chartsStartY - padding - 120);
      const pieDiameter = Math.max(0, Math.min(600, maxPieByWidth, maxPieByHeight));
      const pieR = Math.max(10, Math.round(pieDiameter / 2));
      const pieX = pieAreaX + Math.round(leftSubW / 2);
      const pieY = chartsStartY + pieR;
      const totalVal = graficaUnoData.reduce((s, it) => s + (Number(it.total) || 0), 0) || 1;
      let ang = -Math.PI / 2;
      const palette = opts.palette || ['#f59e0b', '#06b6d4', '#e7d488ff', '#10b981', '#6366f1', '#a78bfa'];
      
      // Collect unique etnias from both data sources and create color map
      const uniqueEtnias = [];
      const etniaSet = new Set();
      // First scan graficaUnoData
      graficaUnoData.forEach(item => {
        const etniaName = item.etnia || '';
        if (etniaName && !etniaSet.has(etniaName)) {
          etniaSet.add(etniaName);
          uniqueEtnias.push(etniaName);
        }
      });
      // Then scan graficaDosData for any missing etnias
      graficaDosData.forEach(item => {
        const etniaName = item.etnia || '';
        if (etniaName && !etniaSet.has(etniaName)) {
          etniaSet.add(etniaName);
          uniqueEtnias.push(etniaName);
        }
      });
      // Map each etnia to a color for consistent use across both charts
      const etniaColorMap = {};
      for (let i = 0; i < uniqueEtnias.length; i++) {
        etniaColorMap[uniqueEtnias[i]] = palette[i % palette.length];
      }
      
      // helper: decide contrasting text color (black/white) for a hex color
      const contrastFor = (hex) => {
        try {
          const h = hex.replace('#','');
          const r = parseInt(h.length===3 ? h[0]+h[0] : h.slice(0,2),16);
          const g = parseInt(h.length===3 ? h[1]+h[1] : h.slice(2,4),16);
          const b = parseInt(h.length===3 ? h[2]+h[2] : h.slice(4,6),16);
          const lum = 0.299*r + 0.587*g + 0.114*b;
          return lum < 140 ? '#0f172a' : '#0f172a';
        } catch (e) { return '#0f172a'; }
      };
      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        const v = Number(it.total) || 0;
        const a = (v / totalVal) * Math.PI * 2;
        const startAng = ang;
        const endAng = ang + a;

        // slice - use etniaColorMap for consistent color
        ctx.beginPath();
        ctx.moveTo(pieX, pieY);
        const etniaName = it.etnia || '';
        const fillColor = etniaColorMap[etniaName] || palette[i % palette.length];
        ctx.fillStyle = fillColor;
        ctx.arc(pieX, pieY, pieR, startAng, endAng);
        ctx.lineTo(pieX, pieY);
        ctx.fill();

        // label on slice: value and percentage, positioned inside slice
        const mid = startAng + a / 2;
        const labelRadius = Math.max(12, Math.round(pieR * 0.62)); // inside slice
        const lx = pieX + Math.cos(mid) * labelRadius;
        const ly = pieY + Math.sin(mid) * labelRadius;
        const pct = Math.round((v / totalVal) * 1000) / 10; // one decimal
        const labelText = (Number.isFinite(v) ? String(v) : '') + (typeof v === 'number' ? ` (${pct}%)` : '');

        ctx.save();
        const baseLabelSize = Math.max(10, Math.round(pieR * 0.12));
        ctx.font = `${baseLabelSize}px Inter, Arial, sans-serif`;
        const maxTextWidth = Math.max(20, Math.round(labelRadius * a * 0.9));
        const textWidth = ctx.measureText(labelText).width;
        const canFitInside = a > 0.12 && textWidth <= maxTextWidth;
        ctx.textBaseline = 'middle';
        if (canFitInside) {
          ctx.textAlign = 'center';
          ctx.fillStyle = contrastFor(fillColor);
          ctx.fillText(labelText, lx, ly);
        } else {
          // keep font size; move label outside the pie if it cannot fit inside
          const outsideR = pieR + 14;
          const ox = pieX + Math.cos(mid) * outsideR;
          const oy = pieY + Math.sin(mid) * outsideR;
          const alignRight = Math.cos(mid) < 0;
          ctx.textAlign = alignRight ? 'right' : 'left';
          ctx.fillStyle = opts.pieOutsideLabelColor || '#0f172a';
          ctx.strokeStyle = opts.pieOutsideLineColor || 'rgba(15,23,42,0.35)';
          ctx.lineWidth = 1;
          const edgeX = pieX + Math.cos(mid) * (pieR - 1);
          const edgeY = pieY + Math.sin(mid) * (pieR - 1);
          const elbowX = pieX + Math.cos(mid) * (pieR + 6);
          const elbowY = pieY + Math.sin(mid) * (pieR + 6);
          ctx.beginPath();
          ctx.moveTo(edgeX, edgeY);
          ctx.lineTo(elbowX, elbowY);
          ctx.lineTo(ox, oy);
          ctx.stroke();
          ctx.fillText(labelText, ox + (alignRight ? -4 : 4), oy);
        }
        ctx.restore();

        ang += a;
      }
      // inner hole (optional) - default is full pie (no hole)
      const innerRadius = typeof opts.pieInnerRadius === 'number' ? opts.pieInnerRadius : 0;
      if (innerRadius > 0) {
        ctx.fillStyle = opts.pieInnerBg || '#ffffff';
        ctx.beginPath();
        ctx.arc(pieX, pieY, Math.round(innerRadius), 0, Math.PI * 2);
        ctx.fill();
      }

      // legend under pie (within left sub-area)
      ctx.font = `${opts.legendFontSize || 14}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      let legendY = pieY + pieR + 8;
      const legendX = pieAreaX + 8;
      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        const etniaName = it.etnia || '';
        ctx.fillStyle = etniaColorMap[etniaName] || palette[i % palette.length];
        ctx.fillRect(legendX, legendY, 12, 12);
        ctx.fillStyle = opts.legendColor || '#111827';
        ctx.fillText(`${it.etnia} (${it.total})`, legendX + 18, legendY - 2);
        legendY += 18;
      }

      // Group graficaDosData by departamento, keeping etnia breakdown
      const groupedByDepartamento = graficaDosData.reduce((acc, item) => {
        const dept = String(item.departamento || '');
        if (!acc[dept]) {
          acc[dept] = { departamento: dept, total: 0, etnias: [] };
        }
        acc[dept].total += Number(item.total) || 0;
        acc[dept].etnias.push({
          etnia: item.etnia || '',
          total: Number(item.total) || 0
        });
        return acc;
      }, {});
      const graficaDosGrouped = Object.values(groupedByDepartamento).slice(0, 3);

      // Draw etnia legend above graficaDos bars
      const legendStartY = pieY - 12;
      const etniaLegendX = barsAreaX + 8;
      ctx.font = `${opts.legendFontSize || 11}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      let legendItemX = etniaLegendX;
      for (let i = 0; i < uniqueEtnias.length; i++) {
        ctx.fillStyle = etniaColorMap[uniqueEtnias[i]];
        ctx.fillRect(legendItemX, legendStartY - 10, 10, 10);
        ctx.fillStyle = opts.legendColor || '#111827';
        const etniaLabel = uniqueEtnias[i];
        ctx.fillText(etniaLabel, legendItemX + 14, legendStartY);
        legendItemX += ctx.measureText(etniaLabel).width + 22;
      }

      // graficaDos: stacked vertical bars inside right sub-area of left column
      const barsX = barsAreaX + 8;
      const barsW = Math.max(0, leftSubW - 16);
      const barsTopY = pieY; // align vertical center with pie top
      const vbarGap = typeof opts.vbarGap === 'number' ? opts.vbarGap : 12;
      const nBars = Math.max(1, graficaDosGrouped.length);
      const maxBarVal = graficaDosGrouped.reduce((m, it) => Math.max(m, Number(it.total) || 0), 0) || 1;
      const totalGap = (nBars - 1) * vbarGap;
      const slotW = Math.max(8, Math.floor((barsW - totalGap) / nBars));
      const vBarMaxH = Math.min(220, Math.max(40, height - barsTopY - padding - 120)); // ensure non-negative

      ctx.font = `${opts.barLabelSize || 14}px Inter, Arial, sans-serif`;
      for (let i = 0; i < nBars; i++) {
        const it = graficaDosGrouped[i] || {};
        const bx = barsX + i * (slotW + vbarGap);
        // bar background (full slot)
        ctx.fillStyle = opts.barBg || '#e6e7ea';
        const barBgX = bx;
        const barBgY = barsTopY;
        ctx.fillRect(barBgX, barBgY, slotW, vBarMaxH);

        // Draw stacked segments for each etnia
        const val = Number(it.total) || 0;
        const totalBarHeight = Math.round((val / maxBarVal) * vBarMaxH);
        let currentY = barsTopY + vBarMaxH; // start from bottom
        
        const etnias = it.etnias || [];
        for (let j = 0; j < etnias.length; j++) {
          const etniaItem = etnias[j];
          const etniaVal = Number(etniaItem.total) || 0;
          const segmentHeight = Math.round((etniaVal / maxBarVal) * vBarMaxH);
          
          if (segmentHeight > 0) {
            const segmentY = currentY - segmentHeight;
            const etniaName = etniaItem.etnia || '';
            ctx.fillStyle = etniaColorMap[etniaName] || palette[j % palette.length];
            ctx.fillRect(bx, segmentY, slotW, segmentHeight);
            currentY = segmentY;
          }
        }

        // value text above bar (total)
        ctx.fillStyle = opts.barValueColor || '#0f172a';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        const barTop = barsTopY + (vBarMaxH - totalBarHeight);
        const valY = barTop - 6;
        ctx.font = `bold ${Math.max(10, (opts.barValueFontSize || 12))}px Inter, Arial, sans-serif`;
        if (valY > barsTopY + 10) {
          ctx.fillText(String(it.total || ''), bx + Math.round(slotW / 2), valY);
        } else {
          ctx.fillStyle = '#ffffff';
          ctx.textBaseline = 'top';
          ctx.fillText(String(it.total || ''), bx + Math.round(slotW / 2), barTop + 6);
        }

        // label under bar (department/category)
        ctx.fillStyle = opts.barLabelColor || '#334155';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const label = String(it.departamento || '');
        const labelY = barsTopY + vBarMaxH + 8;
        const maxLabelW = slotW;
        let labelText = label;
        while (ctx.measureText(labelText).width > maxLabelW && labelText.length > 0) {
          labelText = labelText.slice(0, -1);
        }
        if (labelText.length < label.length) labelText = labelText.slice(0, Math.max(0, labelText.length - 2)) + '...';
        ctx.font = `${opts.barLabelSize || 12}px Inter, Arial, sans-serif`;
        ctx.fillText(labelText, bx + Math.round(slotW / 2), labelY);
      }

      // bottom texts: place textoUno under left area and textoDos under bars area, then footer
      const textoUno = opts.textoUno || props.textoUno || '';
      const textoDos = opts.textoDos || props.textoDos || '';
      const footerText = opts.footerText || props.footerText || '';
      ctx.font = `${opts.bottomFontSize || 20}px Inter, Arial, sans-serif`;
      ctx.fillStyle = opts.bottomColor || '#374151';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const drawSmallWrap = (c, text, x, y0, maxW, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let yPos = y0 + 20;
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxW && line) {
            c.fillText(line, x, yPos);
            line = words[n];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      // textoUno under the pie/legend (left sub-area)
      const leftTextX = pieAreaX + 8;
      const leftTextMaxW = leftSubW - 16;
      const leftTextY = legendY + 20;
      drawSmallWrap(ctx, textoUno, leftTextX, leftTextY, leftTextMaxW, 20);

      // textoDos under the vertical bars (right sub-area inside left column)
      const rightTextX = barsX;
      const rightTextMaxW = barsW - 8;
      const rightTextY = barsTopY + vBarMaxH + 28;
      drawSmallWrap(ctx, textoDos, rightTextX, rightTextY, rightTextMaxW, 20);

      // justified textoRight inside the right column (vertically centered)
      // accept multiple possible prop/opt names
      const textoRightRaw = props.textoRight ?? props.rightText ?? '';
      const textoRight = String(textoRightRaw || '').trim();
      if (textoRight) {
        ctx.save();

        // initial font size and color
        let rightFontSize = typeof opts.textoRightSize === 'number' ? opts.textoRightSize : 30;
        const minFontSize = 8;
        const color = opts.textoRightColor || '#374151';
        ctx.fillStyle = color;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        const rightInset = 12;
        const maxRightW = Math.max(8, rightAreaW - rightInset * 2);
        // vertical bounds for centering: whole column (between padding and height - padding)
        const topBound = padding;
        const bottomBound = height - padding;

        // auto-scale down if block doesn't fit vertically
        let lines = [];
        let lineH = 0;
        let totalH = 0;
        while (rightFontSize >= minFontSize) {
          ctx.font = `${rightFontSize}px Inter, Arial, sans-serif`;
          lines = wrapLines(ctx, textoRight, maxRightW);
          lineH = Math.round(rightFontSize * 1.2);
          totalH = lines.length * lineH;
          if (totalH <= (bottomBound - topBound) || rightFontSize === minFontSize) break;
          rightFontSize -= 1;
        }

        // compute start positions
        const startY = topBound + Math.round(((bottomBound - topBound) - totalH) / 4);
        const startX = rightAreaX + rightInset;

        // draw lines justified
        ctx.fillStyle = color;
        drawJustifiedLines(ctx, lines, startX, startY, maxRightW, lineH);

        ctx.restore();
      }

      ctx.fillStyle = opts.footerColor || '#6b7280';
      ctx.font = `${opts.footerFontSize ?? 25}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'top';

      const footerInset = typeof opts.footerInset === 'number' ? opts.footerInset : 8;
      const footerMaxW = Math.max(0, rightAreaW - footerInset - 8);

      // wrap footer into lines that fit the right column
      const wrapFooter = (c, text, maxW) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = '';
        for (let w of words) {
          const test = cur ? cur + ' ' + w : w;
          if (c.measureText(test).width <= maxW) {
            cur = test;
          } else {
            if (cur) lines.push(cur);
            // long single word fallback
            cur = w;
          }
        }
        if (cur) lines.push(cur);
        return lines;
      };

      const footerLines = wrapFooter(ctx, footerText, footerMaxW);
      const footerLineH = Math.round((opts.footerLineHeight ?? 1.2) * (opts.footerFontSize ?? 25));
      const footerBlockH = footerLines.length * footerLineH;

      // compute target Y: 100px below textoRight bottom if present, else near bottom padding
      const defaultFooterY = height - padding - footerBlockH - 200;
      let footerY = defaultFooterY;
      if (typeof footerYFromRight === 'number') {
        footerY = Math.min(footerYFromRight + 100, defaultFooterY);
      }
      // ensure footer block stays on canvas
      footerY = Math.max(padding, Math.min(footerY, height - padding - footerBlockH));

      const footerX = rightAreaX + rightAreaW - footerInset;
      for (let i = 0; i < footerLines.length; i++) {
        ctx.fillText(footerLines[i], footerX, footerY + i * footerLineH);
      }

      // optional logo bottom-right
      const mainLogoSrc = opts.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.12));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_seis_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});